import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, X, CheckCircle, Clock } from '@phosphor-icons/react';
import { fmtDateTimeFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'batiboys_shown_time_mods_v1';

/**
 * On employee login, show a modal listing recent admin-made modifications
 * to their time entries. Each item can be acknowledged (marked as read).
 * The modal only appears once per browser session (tracked via localStorage).
 */
export const TimeModificationsAlert = ({ user }) => {
  const [mods, setMods] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role === 'admin') return;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications?limit=50`, { withCredentials: true });
        const list = (res.data || []).filter(n => n.type === 'time_modified' && !n.read);
        if (list.length === 0) return;
        // Only show once per session
        const shownIds = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        const fresh = list.filter(n => !shownIds.includes(n.id));
        if (fresh.length > 0) {
          setMods(list);
          setOpen(true);
        }
      } catch { /* ignore */ }
    };
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [user]);

  const ackAll = async () => {
    try {
      await Promise.all(mods.map(m => axios.put(`${API_URL}/api/notifications/${m.id}/read`, {}, { withCredentials: true })));
      const shownIds = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...shownIds, ...mods.map(m => m.id)]));
      toast.success('Modifications prises en compte');
    } catch { /* ignore */ }
    setOpen(false);
  };

  const dismiss = () => {
    const shownIds = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...shownIds, ...mods.map(m => m.id)]));
    setOpen(false);
  };

  if (!open || mods.length === 0) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      data-testid="time-modifications-modal"
    >
      <div style={{ background: 'white', borderRadius: '10px', maxWidth: '520px', width: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', background: 'linear-gradient(135deg, #EF6C00, #E65100)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Warning size={22} weight="bold" />
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Modifications de vos pointages</h3>
          </div>
          <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }} aria-label="Fermer"><X size={20} /></button>
        </div>
        <div style={{ padding: '14px 20px', background: '#FFF3E0', color: '#5E5F66', fontSize: '0.88rem' }}>
          Votre administrateur a modifie ou supprime {mods.length} pointage(s). Verifiez les changements ci-dessous :
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {mods.map(m => (
            <div key={m.id} data-testid={`time-mod-item-${m.id}`} style={{ padding: '12px 14px', border: '1px solid #E2E2E6', borderLeft: '3px solid #EF6C00', borderRadius: '6px', marginBottom: '10px', background: '#FAFAFA' }}>
              <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1E2030', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={14} /> {m.title}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#5E5F66', whiteSpace: 'pre-wrap' }}>{m.message}</div>
              <div style={{ fontSize: '0.72rem', color: '#9E9EA7', marginTop: '6px' }}>{fmtDateTimeFR(m.created_at)}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={dismiss} data-testid="time-mod-later">Plus tard</button>
          <button className="btn btn-primary" onClick={ackAll} data-testid="time-mod-ack" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle size={16} weight="bold" /> J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
};
